import { Skeleton, Stack, useTheme } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { useGoalsRightOptional } from "web_myplann/Api/Rights/RightsProvider";

const sizeScales = {
	sm: 0.75,
	md: 1,
	lg: 1.1,
	xl: 1.25,
} as const;

export type RowSkeletonLoaderSize = keyof typeof sizeScales;

export const RowSkeletonLoader = ({
	delay = 0,
	disableAnimation = false,
	size = "md",
}: {
	delay?: number;
	disableAnimation?: boolean;
	size?: RowSkeletonLoaderSize;
}) => {
	const theme = useTheme();
	const pilotGoal = useGoalsRightOptional();
	const greyScale = pilotGoal
		? theme.palette.grey
		: theme.palette.deprecated.grey;
	const animation: "wave" | "pulse" | false = disableAnimation ? false : "wave";
	const waveSx: SxProps<Theme> = disableAnimation
		? {}
		: { "&::after": { animationDelay: `${delay}ms` } };
	const scale = sizeScales[size];
	const s = (value: number) => Math.round(value * scale);

	return (
		<Stack
			justifyContent={"space-between"}
			alignItems={"center"}
			width={s(250)}
			height={s(56)}
			bgcolor={greyScale[50]}
			direction={"row"}
			p={0.8 * scale}
			borderRadius={0.8}
			boxShadow="0px 3px 8px 0px #0000000D"
			border={theme.colors.border.solid(greyScale[100])}
		>
			<Stack gap={0.4 * scale} direction={"row"}>
				<Stack justifyContent={"center"}>
					<Skeleton
						variant="circular"
						width={s(16)}
						height={s(16)}
						animation={animation}
						sx={[waveSx, { backgroundColor: greyScale[100] }]}
					/>
				</Stack>
				<Stack gap={0.8 * scale}>
					<Stack direction={"row"} alignItems={"center"} gap={0.8 * scale}>
						<Skeleton
							variant="rounded"
							height={s(14)}
							width={s(60)}
							animation={animation}
							sx={[
								{ borderRadius: 1.6, backgroundColor: greyScale[100] },
								waveSx,
							]}
						/>
						<Skeleton
							variant="rounded"
							height={s(8)}
							width={s(8)}
							animation={animation}
							sx={[
								{ borderRadius: 1.6, backgroundColor: greyScale[100] },
								waveSx,
							]}
						/>
					</Stack>
					<Skeleton
						variant="rounded"
						height={s(18)}
						width={s(150)}
						animation={animation}
						sx={[
							{ borderRadius: 1.6, backgroundColor: greyScale[200] },
							waveSx,
						]}
					/>
				</Stack>
			</Stack>
			<Stack>
				<Skeleton
					variant="circular"
					width={s(20)}
					height={s(20)}
					animation={animation}
					sx={[{ backgroundColor: greyScale[100] }, waveSx]}
				/>
			</Stack>
		</Stack>
	);
};
